(() => {
  if (window.__viqzetShippingProtectionV2) return;
  window.__viqzetShippingProtectionV2 = true;

  const MODULE_SELECTOR = '[data-shipping-protection]';
  const CLEANUP_SELECTOR = '[data-shipping-protection-cleanup]';
  const TOGGLE_SELECTOR = '[data-shipping-protection-toggle]';
  const LINE_SELECTOR = '[data-shipping-protection-line]';
  const CART_CONTROL_SELECTOR = [
    'cart-drawer-items quantity-input button',
    'cart-drawer-items input[name="updates[]"]',
    'cart-drawer-items cart-remove-button button',
  ].join(',');
  const CART_ACTION_SELECTOR = [
    'cart-drawer-items quantity-input button',
    'cart-drawer-items cart-remove-button button',
  ].join(',');
  const CART_INPUT_SELECTOR = 'cart-drawer-items input[name="updates[]"]';
  const CONTROL_LOCK_ATTRIBUTE = 'data-shipping-protection-lock';
  const PRODUCT_HANDLE = 'shipping-protection';
  const PREFERENCE_KEY = 'viqzet-shipping-protection-preference-v2';
  const CHANNEL_NAME = 'viqzet-cart-mutation-v1';
  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  const MUTATION_QUIET_TIME = 180;
  const MUTATION_TIMEOUT = 20000;
  const MUTATION_BODY_FALLBACK = 2500;
  const REDUNDANT_UPDATE_WINDOW = 3000;
  const REMOTE_LOCK_TIMEOUT = 22000;
  let busy = false;
  let nativeCartBusy = false;
  let nativeCompletionSeen = false;
  let nativeChangeExpected = false;
  let cooldownUntil = 0;
  let cooldownTimer;
  let initTimer;
  let nativeCartTimer;
  let mutationQuietTimer;
  let remoteLockTimer;
  let remoteRefreshBusy = false;
  let defaultAttempted = false;
  let announcedLocalBusy = false;
  let cachedNativeChangeResponse;
  const activeCartMutations = new Set();
  const remoteLocks = new Map();
  const tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const mutationChannel = typeof BroadcastChannel === 'function'
    ? new BroadcastChannel(CHANNEL_NAME)
    : null;

  const getModule = () => document.querySelector(MODULE_SELECTOR);
  const getCleanup = () => document.querySelector(CLEANUP_SELECTOR);

  const getLines = (container) => {
    if (!container) return [];

    return Array.from(container.querySelectorAll(LINE_SELECTOR))
      .map((line) => ({
        key: line.dataset.key,
        variantId: Number(line.dataset.variantId),
        quantity: Number(line.dataset.quantity || 0),
      }))
      .filter((line) => line.key);
  };

  const setVisual = (module, selected, loading) => {
    if (!module) return;
    const toggle = module.querySelector(TOGGLE_SELECTOR);
    if (!toggle) return;

    toggle.setAttribute('aria-checked', selected ? 'true' : 'false');
    toggle.disabled = Boolean(loading);
    module.setAttribute('aria-busy', loading ? 'true' : 'false');
  };

  const setStatus = (module, message) => {
    const status = module?.querySelector('.shipping-protection__status');
    if (status) status.textContent = message || '';
  };

  const setCartControlsLocked = (locked) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    if (locked) {
      drawer.querySelectorAll(CART_CONTROL_SELECTOR).forEach((control) => {
        if (control.disabled) return;
        control.disabled = true;
        control.setAttribute(CONTROL_LOCK_ATTRIBUTE, 'true');
      });
      return;
    }

    drawer.querySelectorAll(`[${CONTROL_LOCK_ATTRIBUTE}]`).forEach((control) => {
      control.disabled = false;
      control.removeAttribute(CONTROL_LOCK_ATTRIBUTE);
    });
  };

  const clearExpiredRemoteLocks = () => {
    const now = Date.now();
    remoteLocks.forEach((expiresAt, id) => {
      if (expiresAt <= now) remoteLocks.delete(id);
    });
  };

  const hasRemoteLock = () => {
    clearExpiredRemoteLocks();
    return remoteLocks.size > 0;
  };

  const hasLocalCartWork = () => busy || nativeCartBusy || activeCartMutations.size > 0;
  const cartInteractionBusy = () => hasLocalCartWork() || remoteRefreshBusy || hasRemoteLock();

  const announceLocalState = () => {
    if (!mutationChannel) return;
    const localBusy = hasLocalCartWork();
    if (localBusy === announcedLocalBusy) return;

    announcedLocalBusy = localBusy;
    mutationChannel.postMessage({
      type: localBusy ? 'busy' : 'idle',
      id: tabId,
      timestamp: Date.now(),
    });
  };

  const syncInteractiveState = (deferControls = false) => {
    const locked = cartInteractionBusy();
    const updateControls = () => setCartControlsLocked(cartInteractionBusy());

    if (deferControls) {
      Promise.resolve().then(updateControls);
    } else {
      updateControls();
    }

    const module = getModule();
    if (module && !busy) {
      setVisual(
        module,
        module.dataset.inCart === 'true',
        locked || Date.now() < cooldownUntil,
      );
    }

    announceLocalState();
  };

  const finishNativeCartUpdate = (force = false) => {
    if (!nativeCartBusy) return;
    if (!force && (!nativeCompletionSeen || activeCartMutations.size > 0)) return;

    nativeCartBusy = false;
    nativeCompletionSeen = false;
    nativeChangeExpected = false;
    window.clearTimeout(nativeCartTimer);
    syncInteractiveState();

    const module = getModule();
    if (module) setStatus(module, '');

    window.clearTimeout(initTimer);
    initTimer = window.setTimeout(ensureDefault, 160);
  };

  const scheduleMutationQuietCheck = () => {
    window.clearTimeout(mutationQuietTimer);
    if (activeCartMutations.size > 0) return;

    mutationQuietTimer = window.setTimeout(() => {
      if (activeCartMutations.size > 0) return;
      finishNativeCartUpdate();
      syncInteractiveState();
      window.clearTimeout(initTimer);
      initTimer = window.setTimeout(ensureDefault, 160);
    }, MUTATION_QUIET_TIME);
  };

  const beginNativeCartUpdate = (expectsChange = false) => {
    if (cartInteractionBusy()) return false;

    cachedNativeChangeResponse = undefined;
    nativeCartBusy = true;
    nativeCompletionSeen = false;
    nativeChangeExpected = expectsChange;
    syncInteractiveState(true);

    window.clearTimeout(nativeCartTimer);
    nativeCartTimer = window.setTimeout(() => finishNativeCartUpdate(true), MUTATION_TIMEOUT + 5000);
    return true;
  };

  const getCartRequestInfo = (input, options = {}) => {
    const requestUrl = typeof input === 'string' || input instanceof URL
      ? String(input)
      : input?.url;
    const method = String(options.method || input?.method || 'GET').toUpperCase();
    if (!requestUrl || method !== 'POST') return undefined;

    try {
      const url = new URL(requestUrl, window.location.href);
      const match = url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?\/?$/);
      if (url.origin !== window.location.origin || !match) return undefined;

      let body;
      if (typeof options.body === 'string') {
        try {
          body = JSON.parse(options.body);
        } catch (error) {
          body = undefined;
        }
      }

      return {
        action: match[1],
        body,
      };
    } catch (error) {
      return undefined;
    }
  };

  const sameSections = (first, second) => {
    if (!Array.isArray(first) || !Array.isArray(second) || first.length !== second.length) {
      return false;
    }

    const sortedFirst = [...first].sort();
    const sortedSecond = [...second].sort();
    return sortedFirst.every((section, index) => section === sortedSecond[index]);
  };

  const cacheNativeChangePayload = (value, requestInfo, response) => {
    if (requestInfo?.action !== 'change' || !response?.ok) return;

    try {
      const payload = typeof value === 'string' ? JSON.parse(value) : value;
      const requestSections = requestInfo.body?.sections;
      if (!payload?.sections || !Array.isArray(requestSections)) return;

      cachedNativeChangeResponse = {
        raw: typeof value === 'string' ? value : JSON.stringify(value),
        sections: [...requestSections],
        sectionsUrl: requestInfo.body?.sections_url,
        expiresAt: Date.now() + REDUNDANT_UPDATE_WINDOW,
      };
    } catch (error) {
      cachedNativeChangeResponse = undefined;
    }
  };

  const takeRedundantUpdateResponse = (requestInfo) => {
    const cached = cachedNativeChangeResponse;
    const body = requestInfo?.body;
    if (!nativeCartBusy || requestInfo?.action !== 'update' || !cached || !body) return undefined;

    const keys = Object.keys(body);
    const isSectionsOnly = keys.length > 0
      && keys.every((key) => key === 'sections' || key === 'sections_url');
    const isMatchingRefresh = isSectionsOnly
      && sameSections(body.sections, cached.sections)
      && body.sections_url === cached.sectionsUrl
      && Date.now() <= cached.expiresAt;
    if (!isMatchingRefresh) return undefined;

    cachedNativeChangeResponse = undefined;
    return cached.raw;
  };

  const createCachedCartResponse = (raw) => {
    if (typeof window.Response === 'function') {
      return new window.Response(raw, {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    return {
      ok: true,
      status: 200,
      headers: { get: () => 'application/json' },
      text: async () => raw,
      json: async () => JSON.parse(raw),
    };
  };

  const releaseCartMutation = (token) => {
    if (!activeCartMutations.has(token)) return;
    activeCartMutations.delete(token);
    window.clearTimeout(token.timeout);
    scheduleMutationQuietCheck();
    syncInteractiveState();
  };

  const registerCartMutation = () => {
    const token = {};
    window.clearTimeout(mutationQuietTimer);
    activeCartMutations.add(token);
    token.timeout = window.setTimeout(() => releaseCartMutation(token), MUTATION_TIMEOUT);
    syncInteractiveState();
    return token;
  };

  const originalFetch = window.fetch.bind(window);

  const holdMutationUntilBodyRead = (response, token, requestInfo) => {
    let released = false;
    let fallbackTimer;

    const releaseAfterConsumers = () => {
      if (released) return;
      released = true;
      window.clearTimeout(fallbackTimer);
      window.setTimeout(() => releaseCartMutation(token), 0);
    };

    ['text', 'json', 'blob', 'arrayBuffer', 'formData'].forEach((method) => {
      if (typeof response?.[method] !== 'function') return;
      const originalMethod = response[method].bind(response);

      try {
        response[method] = (...args) => {
          try {
            return Promise.resolve(originalMethod(...args))
              .then((value) => {
                if (method === 'text' || method === 'json') {
                  cacheNativeChangePayload(value, requestInfo, response);
                }
                return value;
              })
              .finally(releaseAfterConsumers);
          } catch (error) {
            releaseAfterConsumers();
            throw error;
          }
        };
      } catch (error) {
        // Native Response methods are normally extensible. The fallback still
        // releases the lock if a browser prevents wrapping an instance method.
      }
    });

    fallbackTimer = window.setTimeout(releaseAfterConsumers, MUTATION_BODY_FALLBACK);
    return response;
  };

  window.fetch = (input, options) => {
    const requestInfo = getCartRequestInfo(input, options);
    if (!requestInfo) return originalFetch(input, options);

    const cachedResponse = takeRedundantUpdateResponse(requestInfo);
    if (cachedResponse !== undefined) {
      return Promise.resolve(createCachedCartResponse(cachedResponse));
    }

    const token = registerCartMutation();
    try {
      return Promise.resolve(originalFetch(input, options))
        .then((response) => {
          return holdMutationUntilBodyRead(response, token, requestInfo);
        }, (error) => {
          releaseCartMutation(token);
          throw error;
        });
    } catch (error) {
      releaseCartMutation(token);
      throw error;
    }
  };

  if (mutationChannel) {
    mutationChannel.addEventListener('message', (event) => {
      const message = event.data;
      if (!message || message.id === tabId) return;

      if (message.type === 'hello') {
        if (hasLocalCartWork()) {
          mutationChannel.postMessage({ type: 'busy', id: tabId, timestamp: Date.now() });
        }
        return;
      }

      if (message.type === 'busy') {
        remoteLocks.set(message.id, Date.now() + REMOTE_LOCK_TIMEOUT);
      } else if (message.type === 'idle') {
        remoteLocks.delete(message.id);
        if (!hasRemoteLock()) refreshAfterRemoteMutation();
      } else {
        return;
      }

      window.clearTimeout(remoteLockTimer);
      remoteLockTimer = window.setTimeout(() => {
        clearExpiredRemoteLocks();
        if (hasRemoteLock()) {
          syncInteractiveState();
        } else {
          refreshAfterRemoteMutation();
        }
      }, REMOTE_LOCK_TIMEOUT + 50);
      syncInteractiveState();
    });

    window.addEventListener('pagehide', () => {
      if (announcedLocalBusy) {
        mutationChannel.postMessage({ type: 'idle', id: tabId, timestamp: Date.now() });
      }
    });

    mutationChannel.postMessage({ type: 'hello', id: tabId, timestamp: Date.now() });
  }

  const enforceCooldown = (module) => {
    if (!module) return false;
    const remaining = cooldownUntil - Date.now();
    if (remaining <= 0) return false;

    const selected = module.dataset.inCart === 'true';
    setVisual(module, selected, true);
    window.clearTimeout(cooldownTimer);
    cooldownTimer = window.setTimeout(() => {
      const latestModule = getModule();
      if (!latestModule) return;
      setStatus(latestModule, '');
      syncInteractiveState();
    }, remaining + 80);
    return true;
  };

  const rootPath = () => window.Shopify?.routes?.root || '/';

  const parseResponse = async (response) => {
    const text = (await response.text()).trim();
    let data = null;

    try {
      data = JSON.parse(text);
    } catch (error) {
      // Non-JSON responses are handled as a failed cart request below.
    }

    if (!response.ok || !data || data.status || data.errors) {
      const message = data?.description || data?.message || 'Unable to update Shipping Protection.';
      throw new Error(message);
    }

    return data;
  };

  const postUpdate = async (updates) => {
    const endpoint = `${rootPath()}cart/update.js`;
    const options = {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        updates,
        sections: SECTIONS,
        sections_url: window.location.pathname,
      }),
    };

    return parseResponse(await fetch(endpoint, options));
  };

  const fetchCart = async () => {
    const response = await fetch(`${rootPath()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    return parseResponse(response);
  };

  const fetchRenderedSections = async () => {
    const path = window.location.pathname || rootPath();
    const response = await fetch(`${path}?sections=${SECTIONS.join(',')}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    return parseResponse(response);
  };

  const replaceRenderedSections = (sections, reopenDrawer) => {
    if (!sections) return false;

    const parser = new DOMParser();
    const currentDrawer = document.querySelector('cart-drawer');
    const wasActive = Boolean(currentDrawer?.classList.contains('active'));
    let replaced = false;

    if (typeof sections['cart-drawer'] === 'string' && currentDrawer) {
      const drawerDocument = parser.parseFromString(sections['cart-drawer'], 'text/html');
      const freshDrawer = drawerDocument.querySelector('cart-drawer');
      if (freshDrawer) {
        currentDrawer.innerHTML = freshDrawer.innerHTML;
        replaced = true;
      }
    }

    if (typeof sections['cart-icon-bubble'] === 'string') {
      const bubbleDocument = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
      const freshBubble = bubbleDocument.querySelector('#cart-icon-bubble');
      const currentBubble = document.querySelector('#cart-icon-bubble');
      if (freshBubble && currentBubble) {
        currentBubble.innerHTML = freshBubble.innerHTML;
        const freshLabel = freshBubble.getAttribute('aria-label');
        if (freshLabel) currentBubble.setAttribute('aria-label', freshLabel);
      }
    }

    if (reopenDrawer || wasActive) {
      window.requestAnimationFrame(() => {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) return;
        drawer.classList.add('active');
        document.body.classList.add('overflow-hidden');
      });
    }

    return replaced;
  };

  const refreshAfterRemoteMutation = async () => {
    if (remoteRefreshBusy || hasRemoteLock()) return;

    const drawer = document.querySelector('cart-drawer');
    const reopenDrawer = Boolean(drawer?.classList.contains('active'));
    remoteRefreshBusy = true;
    syncInteractiveState();

    try {
      const sections = await fetchRenderedSections();
      replaceRenderedSections(sections, reopenDrawer);
    } catch (error) {
      // A failed cross-tab refresh must never block the local cart.
    } finally {
      remoteRefreshBusy = false;
      syncInteractiveState();
      window.clearTimeout(initTimer);
      initTimer = window.setTimeout(ensureDefault, 80);
    }
  };

  const actualProtectionState = (cart, variantId) => {
    if (!Array.isArray(cart?.items)) return false;
    return cart.items.some((item) => (
      item.handle === PRODUCT_HANDLE || Number(item.variant_id) === variantId
    ) && Number(item.quantity) > 0);
  };

  const lineRemovalUpdates = (container) => getLines(container).reduce((updates, line) => {
    updates[line.key] = 0;
    return updates;
  }, {});

  const restoreFromCart = async (desiredState, previousState, variantId, reopenDrawer) => {
    try {
      const cart = await fetchCart();
      const actualState = actualProtectionState(cart, variantId);
      let refreshed = false;

      try {
        const sections = await fetchRenderedSections();
        refreshed = replaceRenderedSections(sections, reopenDrawer);
      } catch (error) {
        // The cart JSON remains authoritative if section recovery fails.
      }

      const module = getModule();
      if (module) module.dataset.inCart = String(actualState);
      setVisual(module, actualState, false);
      if (actualState === desiredState) setStatus(module, '');
      return { actualState, matched: actualState === desiredState, refreshed };
    } catch (error) {
      const module = getModule();
      setVisual(module, previousState, false);
      return { actualState: previousState, matched: false, refreshed: false };
    }
  };

  const updateProtection = async (enabled, source) => {
    if (cartInteractionBusy()) return;
    if (Date.now() < cooldownUntil) {
      window.clearTimeout(initTimer);
      initTimer = window.setTimeout(ensureDefault, cooldownUntil - Date.now() + 80);
      return;
    }

    const module = getModule();
    const cleanup = getCleanup();
    const container = module || cleanup;
    const variantId = Number(module?.dataset.variantId || getLines(container)[0]?.variantId);
    if (!container || (!variantId && enabled)) return;

    const previousState = module?.dataset.inCart === 'true';
    const drawer = document.querySelector('cart-drawer');
    const reopenDrawer = Boolean(drawer?.classList.contains('active'));

    if (source === 'user') {
      try {
        sessionStorage.setItem(PREFERENCE_KEY, enabled ? 'on' : 'off');
      } catch (error) {
        // The cart continues to work when sessionStorage is unavailable.
      }
    }

    const updates = enabled
      ? { [String(variantId)]: 1 }
      : Object.keys(lineRemovalUpdates(container)).length
        ? lineRemovalUpdates(container)
        : { [String(variantId)]: 0 };

    busy = true;
    setStatus(module, '');
    setVisual(module, enabled, true);
    syncInteractiveState();

    try {
      const data = await postUpdate(updates);
      if (!replaceRenderedSections(data.sections, reopenDrawer)) {
        throw new Error('The cart drawer could not be refreshed.');
      }
    } catch (error) {
      const recovery = await restoreFromCart(enabled, previousState, variantId, reopenDrawer);
      const latestModule = getModule();
      if (!recovery.matched) {
        setStatus(latestModule, 'Unable to update Shipping Protection. Please try again.');
        if (source === 'user') {
          try {
            sessionStorage.setItem(PREFERENCE_KEY, previousState ? 'on' : 'off');
          } catch (storageError) {
            // No action is required when storage is unavailable.
          }
        }
      }
    } finally {
      busy = false;
      cooldownUntil = Date.now() + 350;
      syncInteractiveState();
      window.clearTimeout(initTimer);
      initTimer = window.setTimeout(ensureDefault, 50);
    }
  };

  const normalizeProtection = async (module) => {
    if (cartInteractionBusy() || !module) return;
    const variantId = Number(module.dataset.variantId);
    const lines = getLines(module);
    const canonical = lines.find((line) => line.variantId === variantId);
    if (!variantId || !canonical) return;

    const drawer = document.querySelector('cart-drawer');
    const reopenDrawer = Boolean(drawer?.classList.contains('active'));
    const updates = lineRemovalUpdates(module);
    updates[canonical.key] = 1;
    busy = true;
    setVisual(module, true, true);
    syncInteractiveState();

    try {
      const data = await postUpdate(updates);
      replaceRenderedSections(data.sections, reopenDrawer);
    } catch (error) {
      await restoreFromCart(true, true, variantId, reopenDrawer);
    } finally {
      busy = false;
      cooldownUntil = Date.now() + 350;
      syncInteractiveState();
      window.clearTimeout(initTimer);
      initTimer = window.setTimeout(ensureDefault, 50);
    }
  };

  function ensureDefault() {
    if (cartInteractionBusy()) {
      syncInteractiveState();
      return;
    }

    const cleanup = getCleanup();
    if (cleanup) {
      defaultAttempted = false;
      updateProtection(false, 'cleanup');
      return;
    }

    const module = getModule();
    if (!module) {
      defaultAttempted = false;
      return;
    }
    if (enforceCooldown(module)) return;

    const selected = module.dataset.inCart === 'true';
    setVisual(module, selected, false);
    setStatus(module, '');

    if (module.dataset.needsNormalization === 'true') {
      normalizeProtection(module);
      return;
    }

    if (selected) {
      defaultAttempted = true;
      return;
    }

    let preference = null;
    try {
      preference = sessionStorage.getItem(PREFERENCE_KEY);
    } catch (error) {
      // Default behavior remains available without sessionStorage.
    }

    if (preference !== 'off' && !defaultAttempted) {
      defaultAttempted = true;
      updateProtection(true, 'default');
    }
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest?.(TOGGLE_SELECTOR);
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      const module = toggle.closest(MODULE_SELECTOR);
      if (!module || cartInteractionBusy()) return;
      updateProtection(toggle.getAttribute('aria-checked') !== 'true', 'user');
      return;
    }

    const cartAction = event.target.closest?.(CART_ACTION_SELECTOR);
    if (!cartAction) return;

    if (
      cartAction.disabled
      || cartAction.classList?.contains('disabled')
      || cartInteractionBusy()
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    beginNativeCartUpdate(cartAction.matches?.('cart-drawer-items quantity-input button'));
  }, true);

  document.addEventListener('change', (event) => {
    if (!event.target.matches?.(CART_INPUT_SELECTOR)) return;

    if (busy || activeCartMutations.size > 0 || hasRemoteLock()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (nativeCartBusy) {
      if (nativeChangeExpected) {
        nativeChangeExpected = false;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    beginNativeCartUpdate(false);
  }, true);

  document.addEventListener('cartQuantityUpdated', () => {
    if (!nativeCartBusy) return;
    nativeCompletionSeen = true;
    scheduleMutationQuietCheck();
  });

  const observer = new MutationObserver(() => {
    if (cartInteractionBusy()) syncInteractiveState();
    if (nativeCartBusy && nativeCompletionSeen && activeCartMutations.size === 0) {
      scheduleMutationQuietCheck();
    }
    window.clearTimeout(initTimer);
    initTimer = window.setTimeout(ensureDefault, 80);
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureDefault, { once: true });
  } else {
    ensureDefault();
  }
})();

